import { HeaderColumnWithBand, PriceListBand } from '../models/types';
import { PriceListPrice } from '../models';
import { getBandFromMinMax } from '../utils/table-utils';

type ColumnHeaderLabel = {
  singular: string;
  plural: string;
};

type ColumnSubHeaderLabel = { perUnitLabel: string; totalPriceLabel: string };

export abstract class BasePriceListBandsService<T extends { prices?: PriceListPrice[] }> {
  /**
   * Returns the sorted price list bands based on the provided data with prices.
   * Example: [
   *   { duration: { min: 1, max: 1 }, unbounded: false, totalPrice: true },
   *   { duration: { min: 2, max: 3 }, unbounded: false, totalPrice: false },
   *   { duration: { min: 8, max: 99 }, unbounded: true, totalPrice: false },
   * ]
   */
  getSortedBands(dataWithPrices: T[]): PriceListBand[] {
    const allPrices: PriceListPrice[] = dataWithPrices.flatMap(({ prices }) => prices);

    // Remove duplicates and sort intervals
    const priceListBands: PriceListBand[] = allPrices.reduce((acc, { duration: { min, max }, unbounded, totalPrice }) => {
      const bandKey = getBandFromMinMax(min, max);
      const alreadyExists = acc.some(({ duration: { min, max } }) => getBandFromMinMax(min, max) === bandKey);
      return alreadyExists ? acc : [...acc, { duration: { min, max }, unbounded, totalPrice }];
    }, [] as PriceListBand[]);

    // Sort bands based on the sum of min and max weeks
    return this.sortPriceListBands(priceListBands);
  }

  /**
   * Generates header columns with bands for a price list. For that, it uses the provided bands and labels.
   *
   * @param bands - The price list bands to generate the header columns from.
   * @param columnHeaderLabel - The label for the columns, containing singular and plural forms.
   * @param columnSubHeaderLabel - Optional sub-header labels for the columns, containing per unit and total price labels.
   *
   * Example:
   * [
   *  { band: '1-1', header: '1 week', subHeader: 'Total price' },
   *  { band: '2-3', header: '2-3 weeks', subHeader: 'Per unit' },
   *  { band: '8-99', header: '8+ weeks', subHeader: 'Per unit' },
   *  { band: '', header: '+ week', subHeader: 'Per unit' },
   *  ]
   */
  getHeaderColumnsWithBands(bands: PriceListBand[], columnHeaderLabel: ColumnHeaderLabel, columnSubHeaderLabel?: ColumnSubHeaderLabel): HeaderColumnWithBand[] {
    return bands.reduce<HeaderColumnWithBand[]>((headerColumns: HeaderColumnWithBand[], price: PriceListBand) => {
      const newHeaderColumns = [...headerColumns];
      const {
        duration: { min, max },
        totalPrice,
      } = price;

      newHeaderColumns.push({
        band: getBandFromMinMax(min, max),
        header: this.getHeader(columnHeaderLabel, price),
        subHeader: this.getSubHeader(columnSubHeaderLabel, totalPrice),
      });

      return newHeaderColumns;
    }, [] as HeaderColumnWithBand[]);
  }

  /**
   * Sorts price list bands by the sum of min and max weeks.
   */
  private sortPriceListBands(prices: PriceListBand[]) {
    return prices.sort(({ duration: { min: minA, max: maxA } }, { duration: { min: minB, max: maxB } }) => {
      const weeksA = minA + maxA;
      const weeksB = minB + maxB;
      return weeksA - weeksB;
    });
  }

  private getHeader(columnHeaderLabel: ColumnHeaderLabel, { duration: { min, max }, unbounded }: PriceListBand) {
    const band = getBandFromMinMax(min, max);
    const label = this.getColumnLabelFromMin(min, columnHeaderLabel);
    let header = `${band} ${label}`;
    if (unbounded) {
      header = `${min || ''}+ ${label}`;
    } else if (min === max) {
      header = `${min} ${label}`;
    }
    return header;
  }

  private getSubHeader(columnSubHeaderLabel: ColumnSubHeaderLabel, totalPrice: boolean) {
    let subHeader = columnSubHeaderLabel?.perUnitLabel;
    if (totalPrice) {
      subHeader = columnSubHeaderLabel?.totalPriceLabel;
    }
    return subHeader;
  }

  private getColumnLabelFromMin(min: number, columnLabel: ColumnHeaderLabel): string {
    return min > 1 ? columnLabel.plural : columnLabel.singular;
  }
}
